import React, { Fragment, useEffect, useState } from 'react';
import {
    Redirect, Route, Switch, useHistory
} from 'react-router-dom';
import { isTablet, isMobile } from 'react-device-detect';
import { connect, useSelector } from 'react-redux';
import { IState } from 'services/root-reducer';
import { actions as appActions } from 'services/app/app-service';

import { routes } from 'routes';
import { EOperationStatus, IOperation } from 'types';
import { getOmjsUrlParams, getOperation, getOperationSafe } from 'selectors';

import { Pictures } from 'components';
import { MobileUiRootScene } from 'mobile-ui/scenes';
import { actions as overlayWindowsActions } from 'services/overlay-windows/overlay-windows-service';
import { AuthorizationRequiredError, BaseAppError } from 'utils/errors';
import clsx from 'clsx';
import { OrientationBlocker } from 'components/orientation-blocker';
import { useTranslation } from 'react-i18next';
import { isMobileOnlyFixed } from 'utils/is-fixed-ismobileonly';
import { FolderCode } from 'project-constants';
import { freeSpace } from 'utils';
import { MapWrapper } from './map';
import { SideBar } from './side-bar';
import { ObjectWindow } from './object-window';
import { ListWindow } from './list-window';
import { CompareWindow } from './compare-window';
import { SubmitApplicationOverlay } from './submit-application';
import { FavWindow } from './favourites-window';
import { MapLinkOverlay } from './overlay-map-link';
import { ObjectLinkOverlay } from './overlay-object-link';
import { FavoritesLayerChooserOverlay } from './overlay-favorites-layer-chooser';
import { ReportErrorOverlay } from './overlay-report-error';
import { OverlayDataUpdate } from './overlay-data-update';
import { OverlayLogin } from './overlay-login';
import { ObjectWindowGenPlany } from './object-window-gen-plany';
import { switchLanguage } from '../utils/lang';

const filtersRouteComponent = () => {
    const urlParams = useSelector(getOmjsUrlParams);
    useEffect(() => {
        appActions.showObjectFiltersWindow();
        return () => {};
    }, []);
    return <Redirect to={routes.map.construct({ container: 'root', ...urlParams })} />;
};

const rootRedirectComponent = () => {
    const urlParams = useSelector(getOmjsUrlParams);
    return <Redirect to={routes.map.construct({ container: 'root', ...urlParams })} />;
};

const appSubmitComponent = () => {
    const urlParams = useSelector(getOmjsUrlParams);
    overlayWindowsActions.showApplicationOverlay({
        objects: []
    });
    return <Redirect to={routes.map.construct({ container: 'root', ...urlParams })} />;
};

interface IRootSceneProps {
    projectId: string;
    mapCode: string;
    mapName: string;
    apiHost: string;
}

interface IRootSceneStateProps {
    initialize: IOperation | null;
    dataLoad: IOperation | null;
}

const mapStateToProps = (state: IState): IRootSceneStateProps => ({
    initialize: getOperation(state, 'APP_INITIALIZE'),
    dataLoad: getOperationSafe(state, 'DATA_LOAD')
});

const RootSceneComponent: React.FC<IRootSceneProps & IRootSceneStateProps> = (props): JSX.Element => {
    const {
        projectId,
        mapCode,
        mapName,
        apiHost,
        initialize,
        dataLoad
    } = props;
    const [ mapControlsContainerRef, setMapControlsContainerRef ] = useState(null);
    const { t, i18n } = useTranslation();

    const history = useHistory();
    routes.applyHistory(history);
    routes.getCurrentRoute();

    useEffect(() => {
        appActions.initialize({ projectId, mapCode, mapName, apiHost });

        if (isMobile) {
            // какое-то костыльное решение из интернета для фикса поворота экрана
            window.addEventListener('orientationchange', () => {
                document.documentElement.style.height = 'initial';
                setTimeout(() => {
                    document.documentElement.style.height = '100%';
                    setTimeout(() => {
                        // this line prevents the content
                        // from hiding behind the address bar
                        window.scrollTo(0, 1);
                    }, 200);
                }, 200);
            });
        }

        if (isTablet) {
            document.body.className = clsx(document.body.className, 'tablet-version');
        }

        if (i18n.language !== 'ru') {
            switchLanguage('ru');
        } else {
            const url = new URL(window.location.href);
            url.searchParams.delete('lng');
            window.location.assign(url.href);
        }
        return () => {};
    }, []);

    if (
        initialize?.status === EOperationStatus.FAIL
        && ((initialize?.error as BaseAppError)?.isBaseAppError)
    ) {
        return <OverlayLogin forceShow />;
    } else {
        if (initialize?.status === EOperationStatus.SUCCESS) {
            if (isMobileOnlyFixed) {
                return (
                    <MobileUiRootScene
                        projectId={projectId}
                        mapCode={mapCode}
                        mapName={mapName}
                        apiHost={apiHost}
                    />
                );
            } else {
                return (
                    <Fragment>
                        <Route
                            path='*'
                            render={() => (
                                <OrientationBlocker
                                    blockCondition={isTablet}
                                    blockOrientation='vertical'
                                    blockText={t('Пожалуйста, поверните ваше устройство горизонтально')}
                                />
                            )}
                        />
                        <Route
                            path="*"
                            render={() => {
                                let { center, zoom, layers } = routes.getCurrentRoute().params;

                                return (
                                    <MapWrapper
                                        centerFromUrl={center}
                                        zoomFromUrl={zoom}
                                        layersFromUrl={layers}
                                        controlsContainerRef={mapControlsContainerRef}
                                    />
                                );
                            }}
                        />
                        {dataLoad.status === EOperationStatus.SUCCESS && (
                            <div className="oms-app-container">
                                <div className="oms-sidebar-wrapper">
                                    <Switch>
                                        <Route {...routes.map} render={() => <SideBar />} />
                                        <Route {...routes.favourites} render={() => <FavWindow />} />
                                        <Route
                                            {...routes.list}
                                            render={({ match }) => {
                                                if (
                                                    match.params.layerCode === FolderCode['GEN_PLANS']
                                                    && i18n.language === 'en'
                                                ) {
                                                    console.warn(
                                                        'На английской версии генпланов нету.'
                                                        + 'Список объектов показываться не будет'
                                                    );
                                                    setTimeout(() => {
                                                        routes.redirect(routes.map, { container: 'root' });
                                                    });
                                                    return null;
                                                } else {
                                                    return (
                                                        <ListWindow layerCode={match.params.layerCode} />
                                                    );
                                                }
                                            }}
                                        />
                                        <Route {...routes.compareObjects} render={() => <CompareWindow />} />
                                        <Route
                                            {...routes.filters}
                                            component={filtersRouteComponent}
                                        />
                                        <Route
                                            {...routes.submitForm}
                                            component={appSubmitComponent}
                                        />
                                        <Route
                                            {...routes.object}
                                            render={({ match }) => {
                                                if (match.params.layerCode === FolderCode['GEN_PLANS_OBJECTS']) {
                                                    if (i18n.language === 'en') {
                                                        console.warn(
                                                            'На английской версии генпланов нету.'
                                                            + 'Окно показываться не будет'
                                                        );
                                                        setTimeout(() => {
                                                            routes.redirect(routes.map, { container: 'root' });
                                                        });
                                                        return null;
                                                    } else {
                                                        return (
                                                            <ObjectWindowGenPlany />
                                                        );
                                                    }
                                                } else {
                                                    return (
                                                        <ObjectWindow />
                                                    );
                                                }
                                            }}
                                        />
                                        <Route
                                            {...routes.objectShort}
                                            render={({ match }) => {
                                                if (match.params.layerCode === FolderCode['GEN_PLANS_OBJECTS']) {
                                                    if (i18n.language === 'en') {
                                                        console.warn(
                                                            'На английской версии генпланов нету.'
                                                            + 'Окно показываться не будет'
                                                        );
                                                        setTimeout(() => {
                                                            routes.redirect(routes.map, { container: 'root' });
                                                        });
                                                        return null;
                                                    } else {
                                                        return (
                                                            <ObjectWindowGenPlany zoomOnMount />
                                                        );
                                                    }
                                                } else {
                                                    return (
                                                        <ObjectWindow zoomOnMount />
                                                    );
                                                }
                                            }}
                                        />
                                        <Route
                                            path="*"
                                            component={rootRedirectComponent}
                                        />
                                    </Switch>
                                </div>
                                <div
                                    className="oms-map-controls"
                                    ref={el => {
                                        freeSpace.setElement(el);
                                        setMapControlsContainerRef(el);
                                    }}
                                >
                                </div>

                                {/* Окно заявки на производство */}
                                <SubmitApplicationOverlay />

                                {/* Окно ссылки на карту */}
                                <MapLinkOverlay />

                                {/* Окно ссылки на объект */}
                                <ObjectLinkOverlay />

                                {/* Окно выбора слоя для экспорта объектов */}
                                <FavoritesLayerChooserOverlay />

                                {/* Окно "сообщить об ошибке" */}
                                <ReportErrorOverlay />

                                {/* Окно заявки на обновление данных */}
                                <OverlayDataUpdate />

                                {/* Окно логина */}
                                <OverlayLogin />
                            </div>
                        )}
                        <a href="https://orbisystems.ru/" className="oms-orbis-logo">
                            <Pictures.OrbisLogo />
                        </a>
                    </Fragment>
                );
            }
        } else {
            return null;
        }
    }
};

export const RootScene = connect(mapStateToProps)(RootSceneComponent);
